import "./HierarchicalReviewSurface.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { FloatingActionToolbar } from "../../components/FloatingActionToolbar/FloatingActionToolbar.jsx";
import {
  buildFocusContextKey,
  useSlashActionToolbar
} from "../../components/FloatingActionToolbar/useSlashActionToolbar.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";

function getNextSortState(sortState, columnKey) {
  if (sortState?.key !== columnKey) {
    return { key: columnKey, direction: "asc" };
  }

  if (sortState.direction === "asc") {
    return { key: columnKey, direction: "desc" };
  }

  return null;
}

function didSortStateChange(previousSortState, nextSortState) {
  return (
    (previousSortState?.key ?? null) !== (nextSortState?.key ?? null)
    || (previousSortState?.direction ?? null) !== (nextSortState?.direction ?? null)
  );
}

function getHeaderClassName(headerVariant) {
  return [
    "hierarchical-review-header",
    headerVariant === "plain" ? "hierarchical-review-header--plain" : ""
  ].filter(Boolean).join(" ");
}

function getHeaderCellClassName(headerVariant, align, isSortable) {
  return [
    "hierarchical-review-header-cell",
    headerVariant === "plain" ? "hierarchical-review-header-cell--plain" : "",
    align ? `hierarchical-review-header-cell--${align}` : "",
    isSortable ? "sortable" : ""
  ].filter(Boolean).join(" ");
}

function renderSurfaceHeader({
  headerCells,
  headerVariant,
  onSortChange,
  renderHeaderSummaryRow,
  sortState,
  toggleSort
}) {
  if (!headerCells) {
    return null;
  }

  return (
    <thead class={getHeaderClassName(headerVariant)}>
      <tr>
        {headerCells.map((headerCell) => {
          const sortKey = headerCell.sortKey ?? headerCell.key;
          const isSortable = Boolean(headerCell.sortable && sortKey && onSortChange);
          const isSorted = isSortable && sortState?.key === sortKey && sortState.direction;

          return (
            <th
              key={headerCell.key}
              scope="col"
              class={getHeaderCellClassName(headerVariant, headerCell.align, isSortable)}
              onClick={isSortable ? () => toggleSort(sortKey) : undefined}
            >
              <span class="hierarchical-review-header-label">{headerCell.label}</span>
              {isSorted ? (
                <span class="hierarchical-review-sort-indicator">
                  {sortState.direction === "asc" ? "▲" : "▼"}
                </span>
              ) : null}
            </th>
          );
        })}
      </tr>
      {renderHeaderSummaryRow?.({
        headerClassName: getHeaderClassName(headerVariant),
        headerCellClassName: getHeaderCellClassName(headerVariant)
      })}
    </thead>
  );
}

function canOpenSurfaceToolbar(surfaceFocused, selectedRow, selectedLabelRef, toolbarActions) {
  return Boolean(surfaceFocused && selectedRow && selectedLabelRef.current && toolbarActions.length > 0);
}

function buildSurfaceBindings({
  collapseSelectedRow,
  expandSelectedRow,
  moveSelection,
  isRtl,
  rows,
  selectedRowIndex,
  setSelectedIndex,
  setToolbarOpen,
  toggleSelectedRow
}) {
  return [
    onKey("Control+ArrowUp", () => moveSelection(0)),
    onKey("Control+ArrowDown", () => moveSelection(Math.max(0, rows.length - 1))),
    onKey("ArrowUp", () => moveSelection(selectedRowIndex - 1)),
    onKey("ArrowDown", () => moveSelection(selectedRowIndex + 1)),
    onKey(isRtl ? "ArrowLeft" : "ArrowRight", () => expandSelectedRow()),
    onKey(isRtl ? "ArrowRight" : "ArrowLeft", () => collapseSelectedRow()),
    onKey("/", () => {
      setSelectedIndex(0);
      setToolbarOpen(true);
    }),
    onKey("Enter", () => toggleSelectedRow()),
    onKey("Space", () => toggleSelectedRow())
  ];
}

function scrollSelectedRowIntoView(surfaceElement, selectedRowIndex) {
  const selectedElement = surfaceElement?.querySelector(`[data-row-index="${selectedRowIndex}"]`);
  selectedElement?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });

  if (!selectedElement || !surfaceElement) {
    return;
  }

  const header = surfaceElement.querySelector("thead");
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const surfaceRect = surfaceElement.getBoundingClientRect();
  const rowRect = selectedElement.getBoundingClientRect();
  const topBoundary = surfaceRect.top + headerHeight;

  if (rowRect.top < topBoundary) {
    surfaceElement.scrollTop -= (topBoundary - rowRect.top);
    return;
  }

  if (rowRect.bottom > surfaceRect.bottom) {
    surfaceElement.scrollTop += (rowRect.bottom - surfaceRect.bottom);
  }
}

function findNextSelectableRowId(rows, nextIndex) {
  const boundedIndex = Math.max(0, Math.min(rows.length - 1, nextIndex));
  return rows[boundedIndex]?.id ?? null;
}

function findParentRowId(rows, selectedRowIndex, selectedRow) {
  if (!selectedRow) {
    return null;
  }

  for (let index = selectedRowIndex - 1; index >= 0; index -= 1) {
    const candidateRow = rows[index];
    if (candidateRow.depth === selectedRow.depth - 1) {
      return candidateRow.id;
    }
  }

  return null;
}

function useEnsureSelectedRow({ onSelectedRowIdChange, rows, selectedRowId, selectedRowIndex }) {
  useEffect(() => {
    if (rows.length === 0) {
      if (selectedRowId !== null) {
        onSelectedRowIdChange?.(null);
      }
      return;
    }

    if (selectedRowIndex >= 0) {
      return;
    }

    onSelectedRowIdChange?.(rows[0].id);
  }, [onSelectedRowIdChange, rows, selectedRowId, selectedRowIndex]);
}

function useReselectAfterSort({ onSelectedRowIdChange, rows, sortState }) {
  const previousSortStateRef = useRef(sortState);

  useEffect(() => {
    const previousSortState = previousSortStateRef.current;
    previousSortStateRef.current = sortState;

    if (!didSortStateChange(previousSortState, sortState)) {
      return;
    }

    if (rows.length === 0) {
      return;
    }

    onSelectedRowIdChange?.(rows[0].id);
  }, [onSelectedRowIdChange, rows, sortState]);
}

function useScrollSelectedRow(surfaceRef, selectedRowIndex) {
  useEffect(() => {
    if (selectedRowIndex < 0) {
      return;
    }

    const surfaceElement = surfaceRef.current;
    if (!surfaceElement) {
      return;
    }

    scrollSelectedRowIntoView(surfaceElement, selectedRowIndex);
  }, [selectedRowIndex, surfaceRef]);
}

function renderSurfaceBody({
  rows,
  baseClassName,
  emptyContent,
  resolvedHeader,
  renderRow,
  selectedRowId,
  selectedLabelRef,
  handleSelectRow,
  onToggleRow,
  renderedToolbarActions,
  canOpenContextToolbar,
  toolbarOpen,
  toolbarPlacement,
  toolbarOffset,
  toolbarActions,
  setSelectedIndex,
  setToolbarOpen
}) {
  if (rows.length === 0) {
    return <div class={`${baseClassName}-empty`}>{emptyContent}</div>;
  }

  return (
    <>
      <table class={`${baseClassName}-table`}>
        {resolvedHeader}
        <tbody>
          {rows.map((row, rowIndex) => renderRow({
            row,
            rowIndex,
            isSelected: row.id === selectedRowId,
            labelRef: row.id === selectedRowId ? selectedLabelRef : null,
            onSelect: handleSelectRow,
            onToggle: onToggleRow
          }))}
        </tbody>
      </table>
      <FloatingActionToolbar
        actions={renderedToolbarActions}
        anchorElement={canOpenContextToolbar ? selectedLabelRef.current : null}
        isOpen={toolbarOpen && canOpenContextToolbar}
        placement={toolbarPlacement}
        offset={toolbarOffset}
        onActionClick={(actionKey) => {
          const actionIndex = toolbarActions.findIndex((action) => action.key === actionKey);
          if (actionIndex < 0) {
            return;
          }

          setSelectedIndex(actionIndex);
          setToolbarOpen(false);
          toolbarActions[actionIndex]?.onSelect?.();
        }}
      />
    </>
  );
}

export function HierarchicalReviewSurface({
  rows = [],
  selectedRowId = null,
  onSelectedRowIdChange,
  onToggleRow,
  headerCells = null,
  renderHeader,
  renderHeaderSummaryRow,
  headerVariant = "table",
  sortState = null,
  onSortChange = null,
  renderRow,
  emptyContent,
  baseClassName,
  dataRole,
  surfaceRef = null,
  showAltHints = false,
  altHintLabel = "W",
  toolbarPlacement = "inline-end",
  toolbarOffset = 24,
  getToolbarActions = () => []
}) {
  const fallbackSurfaceRef = useRef(null);
  const selectedLabelRef = useRef(null);
  const [surfaceFocused, setSurfaceFocused] = useState(false);
  const effectiveSurfaceRef = surfaceRef ?? fallbackSurfaceRef;
  const selectedRowIndex = useMemo(
    () => rows.findIndex((row) => row.id === selectedRowId),
    [rows, selectedRowId]
  );
  const selectedRow = selectedRowIndex >= 0 ? rows[selectedRowIndex] : null;
  const surfaceIsRtl = document?.documentElement?.dir === "rtl";
  const focusContext = useMemo(() => ({
    row: selectedRow,
    rowIndex: selectedRowIndex,
    colIndex: 0,
    column: { key: "label" },
    editing: false,
    focused: surfaceFocused
  }), [selectedRow, selectedRowIndex, surfaceFocused]);
  const focusContextKey = buildFocusContextKey(focusContext);
  const toolbarActions = useMemo(
    () => (selectedRow ? getToolbarActions(selectedRow) : []),
    [getToolbarActions, selectedRow]
  );
  const canOpenContextToolbar = canOpenSurfaceToolbar(
    surfaceFocused,
    selectedRow,
    selectedLabelRef,
    toolbarActions
  );
  const {
    setSelectedIndex,
    setToolbarOpen,
    toolbarActions: renderedToolbarActions,
    toolbarOpen
  } = useSlashActionToolbar({
    canOpen: canOpenContextToolbar,
    actions: toolbarActions,
    focusContextKey,
    gridFocused: surfaceFocused
  });

  const focusSurface = useCallback(() => {
    effectiveSurfaceRef.current?.focus({ preventScroll: true });
  }, [effectiveSurfaceRef]);

  const handleSelectRow = useCallback((rowId) => {
    onSelectedRowIdChange?.(rowId);
    focusSurface();
  }, [focusSurface, onSelectedRowIdChange]);

  const toggleSort = useCallback((columnKey) => {
    if (!onSortChange) {
      return;
    }

    onSortChange(getNextSortState(sortState, columnKey));
  }, [onSortChange, sortState]);

  useEnsureSelectedRow({ onSelectedRowIdChange, rows, selectedRowId, selectedRowIndex });
  useReselectAfterSort({ onSelectedRowIdChange, rows, sortState });
  useScrollSelectedRow(effectiveSurfaceRef, selectedRowIndex);

  const moveSelection = useCallback((nextIndex) => {
    const nextRowId = findNextSelectableRowId(rows, nextIndex);
    if (!nextRowId) {
      return;
    }

    onSelectedRowIdChange?.(nextRowId);
  }, [onSelectedRowIdChange, rows]);

  const expandSelectedRow = useCallback(() => {
    if (!selectedRow?.children?.length || selectedRow.expanded) {
      return;
    }

    onToggleRow?.(selectedRow.id);
  }, [onToggleRow, selectedRow]);

  const collapseSelectedRow = useCallback(() => {
    if (!selectedRow) {
      return;
    }

    if (selectedRow.children?.length && selectedRow.expanded) {
      onToggleRow?.(selectedRow.id);
      return;
    }

    const parentRowId = findParentRowId(rows, selectedRowIndex, selectedRow);
    if (parentRowId) {
      onSelectedRowIdChange?.(parentRowId);
    }
  }, [onSelectedRowIdChange, onToggleRow, rows, selectedRow, selectedRowIndex]);

  const toggleSelectedRow = useCallback(() => {
    if (!selectedRow?.children?.length) {
      return;
    }

    onToggleRow?.(selectedRow.id);
  }, [onToggleRow, selectedRow]);

  const surfaceBindings = buildSurfaceBindings({
    collapseSelectedRow,
    expandSelectedRow,
    moveSelection,
    isRtl: surfaceIsRtl,
    rows,
    selectedRowIndex,
    setSelectedIndex,
    setToolbarOpen,
    toggleSelectedRow
  });

  useShortcutBindings(effectiveSurfaceRef, surfaceBindings);
  const resolvedHeader = renderSurfaceHeader({
    headerCells,
    headerVariant,
    onSortChange,
    renderHeaderSummaryRow,
    sortState,
    toggleSort
  }) ?? renderHeader?.({
    headerClassName: getHeaderClassName(headerVariant),
    headerCellClassName: getHeaderCellClassName(headerVariant)
  });

  return (
    <AltHintTarget
      label={altHintLabel}
      visible={showAltHints}
      fill
      className={`${baseClassName}-table-wrap`}
      targetRef={effectiveSurfaceRef}
      tabIndex={0}
      data-role={dataRole}
      onFocusIn={() => {
        setSurfaceFocused(true);
      }}
      onFocusOut={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }

        setSurfaceFocused(false);
      }}
    >
      {renderSurfaceBody({
        rows,
        baseClassName,
        emptyContent,
        resolvedHeader,
        renderRow,
        selectedRowId,
        selectedLabelRef,
        handleSelectRow,
        onToggleRow,
        renderedToolbarActions,
        canOpenContextToolbar,
        toolbarOpen,
        toolbarPlacement,
        toolbarOffset,
        toolbarActions,
        setSelectedIndex,
        setToolbarOpen
      })}
    </AltHintTarget>
  );
}
